




















































































import { Component, Vue } from "vue-property-decorator";
import { Route } from "vue-router";
import router from "@/router/router";
import ContentStateComponent from "@/components/common/ContentState.vue";
import { EMPTY_VALUE } from "@/utils/ClientAdministrationDetail";
import { COLOR_LIST_ROW_HOVER_DARK, COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import {
  getLastVisitedItem,
  LAST_VISITED_ITEM_BACKGROUND_STYLE,
  setLastVisitedItem,
  StorageKeyLastVisitedItemLevel,
} from "@/utils/Utils";
import { ComponentName } from "@/utils/RouteGuards";
import { DeviceReportDeviceCategory } from "@/data/models/DeviceReport";
import { DeviceReportRow, isPassResult } from "@/utils/DeviceReportDisplay";
import { DEVICE_REPORT_PAGE_SIZE } from "@/data/repository/DeviceReportRepository";
import {
  formatDateToHoursAndMinuteString,
  formatDateTohDateMonthYearString,
} from "@/utils/DateHelper";
import { fromQueryParams, toQueryParams } from "@/data/models/DeviceReportFilters";
import { loadDeviceReportRows } from "@/data/repository/DeviceReportLoader";
import { themed } from "@/utils/Theme";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";

/**
 * The reports matching the filter screen's question, attached and waiting ones in one list.
 *
 * They are two different records underneath — a Firestore document and a Cloud Storage object — and
 * merging them is the point: "show me everything about this device" should not depend on whether
 * the device existed when its report was uploaded.
 *
 * Filters travel in the URL, so the screen survives a refresh and can be linked to.
 */
@Component({
  // Explicit, so Scaffold's keep-alive still matches it once the build minifies the class name.
  name: "DeviceReportResultsComponent",
  components: { "content-state": ContentStateComponent },
})
export default class DeviceReportResultsComponent extends Vue {
  readonly emptyValue = EMPTY_VALUE;
  /** The report opened last, read once from session storage (Vue cannot track storage). */
  lastVisitedKey: string | null = null;
  rows: DeviceReportRow[] = [];
  isLoading = true;
  isPartial = false;
  loadError = false;

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  /** The column headings show only above rows. */
  get hasRows(): boolean {
    return !this.isLoading && !this.loadError && this.rows.length > 0;
  }

  /** Hover wins over the last-opened tint, so the row under the pointer always responds. */
  rowStyle(row: DeviceReportRow, hover: boolean): string {
    const background = hover ? themed("", COLOR_LIST_ROW_HOVER_DARK) : "";
    if (background) {
      return `background-color: ${background}`;
    }
    return this.lastVisitedKeyOf(row) === this.lastVisitedKey ? LAST_VISITED_ITEM_BACKGROUND_STYLE : "";
  }

  lastVisitedKeyOf(row: DeviceReportRow): string {
    return `deviceReport:${row.key}`;
  }

  get pageSize(): number {
    return DEVICE_REPORT_PAGE_SIZE;
  }

  get countValue(): string {
    // A grey "–" while loading, never "keine" before anything has been counted.
    if (this.isLoading) {
      return "–";
    }

    if (this.rows.length === 0) {
      return this.$t("admin.deviceReports.countNone").toString();
    }

    return this.isPartial
      ? this.$t("admin.deviceReports.countApprox", [this.rows.length]).toString()
      : String(this.rows.length);
  }

  categoryLabel(category: string): string {
    switch (category) {
      case DeviceReportDeviceCategory.COM_BOX:
        return this.$t("admin.deviceReports.categoryComBox").toString();
      case DeviceReportDeviceCategory.SENSOR:
        return this.$t("admin.deviceReports.categorySensor").toString();
      default:
        return this.$t("admin.deviceReports.categoryPanel").toString();
    }
  }

  /** Which identifier the report is still waiting under, which is what an operator needs to act. */
  waitingLabel(row: DeviceReportRow): string {
    return row.serialNo
      ? this.$t("admin.deviceReports.waitingBySerialNumber").toString()
      : this.$t("admin.deviceReports.waitingByMacAddress").toString();
  }

  resultColor(row: DeviceReportRow): string {
    return isPassResult(row.overallResult) ? "success" : "error";
  }

  /** Captured in UTC, shown in local time. */
  formatDate(date: Date | null): string {
    if (!date) {
      return EMPTY_VALUE;
    }

    return `${formatDateTohDateMonthYearString(date)} ${formatDateToHoursAndMinuteString(date)}`;
  }

  onOpenReport(row: DeviceReportRow) {
    this.lastVisitedKey = this.lastVisitedKeyOf(row);
    setLastVisitedItem(this.lastVisitedKey, StorageKeyLastVisitedItemLevel.LEVEL_1);
    router.push({
      name: ComponentName.DEVICE_REPORT_VIEWER,
      query: {
        location: row.location,
        subject: row.serialNo || row.macAddress || "",
        reportId: row.id,
      },
    });
  }

  /** Back to the filters that produced this list, not to an empty form. */
  onBackToFilters() {
    router.push({
      name: ComponentName.DEVICE_REPORT_LIST,
      query: toQueryParams(fromQueryParams(this.$route.query as Record<string, unknown>)),
    });
  }

  async load() {
    this.isLoading = true;
    this.loadError = false;

    try {
      const result = await loadDeviceReportRows(
        fromQueryParams(this.$route.query as Record<string, unknown>)
      );

      this.rows = result.rows;
      this.isPartial = result.isPartial;
    } catch {
      // Swallowed on purpose: the screen shows its own failure state, and the refresh button is the
      // only useful action.
      this.loadError = true;
      this.rows = [];
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Kept alive per search (the filters are its address). Back from a report shows the rows as they
   * were; arriving from the filters is a new search, run again even when its filters match a cached one.
   */
  beforeRouteEnter(_to: Route, from: Route, next: (callback: (vm: Vue) => void) => void) {
    next((vm) => {
      const results = vm as DeviceReportResultsComponent;
      // Not on the first visit, where mounted() has already started the load.
      if (from.name !== ComponentName.DEVICE_REPORT_VIEWER && !results.isLoading) {
        results.load();
      }
    });
  }

  updateToolbarTitle() {
    ToolbarContentModule.setToolbarTitle(
      `${this.$t("admin.deviceReports.title")} > ${this.$t("admin.deviceReports.resultsTitle")}`
    );
  }

  mounted() {
    this.updateToolbarTitle();
    this.lastVisitedKey = getLastVisitedItem(StorageKeyLastVisitedItemLevel.LEVEL_1);
    this.load();
  }

  activated() {
    this.updateToolbarTitle();
    this.lastVisitedKey = getLastVisitedItem(StorageKeyLastVisitedItemLevel.LEVEL_1);
  }
}
